import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { fetchWithAuth } from '../utils/auth';
import { getWsApiBaseUrl } from '../config/api';

type AppealRow = {
  AppealID: string;
  DecisionID: string;
  ViolationID?: string;
  UserID: string;
  Status: string;
  ReasonCode?: string;
  Statement?: string;
  StatementLocale?: string;
  SubmittedAt: string;
  DueAt: string;
  ReviewerUserID?: string;
  decision?: {
    Code?: string;
    Severity?: string;
    Source?: string;
    ActorUserID?: string;
    DecidedAt?: string;
    ReasonText?: string;
  } | null;
  violation?: { Level?: number; BreachOfCommitment?: boolean; Status?: string } | null;
};

const STATUS_TABS: { key: string; label: string }[] = [
  { key: 'OPEN', label: 'Open' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'UPHELD', label: 'Upheld' },
  { key: 'ALL', label: 'All' },
];

/** Zero-tolerance codes never age out and are always shown first. */
const ZERO_CODES = new Set(['MINOR', 'TERROR']);

function dueLabel(dueAt: string): { text: string; tone: 'over' | 'soon' | 'ok' } {
  const ms = new Date(dueAt).getTime() - Date.now();
  const abs = Math.abs(ms);
  const h = Math.floor(abs / 3_600_000);
  const m = Math.floor((abs % 3_600_000) / 60_000);
  const text = h >= 1 ? `${h}h ${m}m` : `${m}m`;
  if (ms < 0) return { text: `${text} over`, tone: 'over' };
  if (ms < 4 * 3_600_000) return { text: `${text} left`, tone: 'soon' };
  return { text: `${text} left`, tone: 'ok' };
}

const Appeals: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AppealRow[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('OPEN');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(
        `${getWsApiBaseUrl()}/admin/enforcement/appeals?status=${status}&limit=100`
      );
      const json = await res.json();
      if (!json?.status) throw new Error(json?.message || 'Failed to load appeals');
      setRows(json.data?.appeals || []);
      setTotal(json.data?.total || 0);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load appeals');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const overdue = rows.filter((r) => new Date(r.DueAt).getTime() < Date.now()).length;
    // A decision no human ever looked at is the one most likely to be wrong, and the
    // MINOR classifier has fired 91 times with nobody ever checking.
    const neverReviewed = rows.filter((r) => !r.decision?.ActorUserID).length;
    const zero = rows.filter((r) => ZERO_CODES.has(r.decision?.Code || '')).length;
    return { overdue, neverReviewed, zero };
  }, [rows]);

  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const az = ZERO_CODES.has(a.decision?.Code || '') ? 0 : 1;
        const bz = ZERO_CODES.has(b.decision?.Code || '') ? 0 : 1;
        if (az !== bz) return az - bz;
        return new Date(a.DueAt).getTime() - new Date(b.DueAt).getTime();
      }),
    [rows]
  );

  return (
    <div className='min-h-screen bg-[#05060A]'>
      <Navbar />
      <div className='max-w-[1920px] mx-auto px-3 sm:px-6 pt-4 pb-8'>
        <div className='flex flex-wrap items-end justify-between gap-4 mb-4'>
          <div>
            <h1 className='text-xl font-semibold text-white'>Appeals</h1>
            <p className='text-xs text-gray-500 mt-1'>
              A creator cannot post or go live while their appeal is open. Answer within 24 hours.
            </p>
          </div>
          <div className='flex gap-2'>
            <Stat label='Overdue' value={stats.overdue} tone={stats.overdue ? 'bad' : 'plain'} />
            <Stat label='Open' value={total} tone='plain' />
            <Stat label='Never human-checked' value={stats.neverReviewed} tone={stats.neverReviewed ? 'warn' : 'plain'} />
            <Stat label='Zero tolerance' value={stats.zero} tone={stats.zero ? 'bad' : 'plain'} />
          </div>
        </div>

        <div className='flex gap-2 mb-4'>
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setStatus(t.key)}
              className={`h-8 px-3 rounded-lg text-xs font-medium border transition ${
                status === t.key
                  ? 'bg-[#FF9C15]/10 border-[#FF9C15]/50 text-white'
                  : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
              }`}
            >
              {t.label}
            </button>
          ))}
          <button
            onClick={load}
            className='h-8 px-3 rounded-lg text-xs font-medium bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 ml-auto'
          >
            Refresh
          </button>
        </div>

        <div className='bg-white/5 border border-white/10 rounded-xl overflow-hidden'>
          <table className='w-full'>
            <thead>
              <tr className='border-b border-white/10'>
                <Th className='w-28'>Due</Th>
                <Th className='w-48'>Creator</Th>
                <Th className='w-36'>Removed for</Th>
                <Th className='w-28'>Record</Th>
                <Th>What they say</Th>
                <Th className='w-32'>First decided by</Th>
                <Th className='w-24' />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className='px-3 py-8 text-center text-sm text-gray-500'>
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && sorted.length === 0 && (
                <tr>
                  <td colSpan={7} className='px-3 py-8 text-center text-sm text-gray-500'>
                    Nothing here.
                  </td>
                </tr>
              )}
              {!loading &&
                sorted.map((r) => {
                  const due = dueLabel(r.DueAt);
                  const isZero = ZERO_CODES.has(r.decision?.Code || '');
                  const neverHuman = !r.decision?.ActorUserID;
                  return (
                    <tr key={r.AppealID} className='border-b border-white/5 hover:bg-white/[0.03]'>
                      <td className='px-3 py-3 align-middle'>
                        <div
                          className={`text-xs font-semibold tabular-nums ${
                            due.tone === 'over' ? 'text-red-400' : due.tone === 'soon' ? 'text-amber-400' : 'text-gray-400'
                          }`}
                        >
                          {due.text}
                        </div>
                        <div className='text-[10px] text-gray-600 font-mono mt-0.5'>{r.AppealID}</div>
                      </td>
                      <td className='px-3 py-3 align-middle'>
                        <div className='text-xs font-semibold text-white'>{r.UserID}</div>
                        <div className='text-[10px] text-gray-500 mt-0.5'>
                          {new Date(r.SubmittedAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className='px-3 py-3 align-middle'>
                        <Badge tone={isZero ? 'zero' : 'severe'}>{r.decision?.Code || '—'}</Badge>
                      </td>
                      <td className='px-3 py-3 align-middle'>
                        {r.violation?.BreachOfCommitment ? (
                          <Badge tone='zero'>BREACH {r.violation?.Level}</Badge>
                        ) : (
                          <Badge tone='plain'>{r.violation?.Level ? `${r.violation.Level} of 3` : '—'}</Badge>
                        )}
                      </td>
                      <td className='px-3 py-3 align-middle'>
                        <div className='text-xs text-gray-300 line-clamp-2 max-w-[420px]'>
                          {r.StatementLocale && (
                            <span className='text-[9px] font-mono text-gray-500 border border-white/10 rounded px-1 py-0.5 mr-1.5 uppercase'>
                              {r.StatementLocale}
                            </span>
                          )}
                          {r.Statement || <span className='text-gray-600'>No statement</span>}
                        </div>
                      </td>
                      <td className='px-3 py-3 align-middle'>
                        <div className={`text-xs ${neverHuman ? 'text-amber-400' : 'text-gray-300'}`}>
                          {neverHuman ? 'Automatic' : r.decision?.ActorUserID}
                        </div>
                        <div className='text-[10px] text-gray-600 mt-0.5'>
                          {neverHuman ? 'no human check' : new Date(r.decision?.DecidedAt || '').toLocaleDateString()}
                        </div>
                      </td>
                      <td className='px-3 py-3 align-middle text-right'>
                        <button
                          onClick={() => navigate(`/appeals/${r.AppealID}`)}
                          className={`h-7 px-3 rounded-lg text-xs font-semibold ${
                            due.tone === 'over' || isZero
                              ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white'
                              : 'bg-white/6 border border-white/15 text-white'
                          }`}
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Th: React.FC<{ children?: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <th className={`text-left text-[10px] font-bold uppercase tracking-wider text-gray-500 px-3 pb-2 pt-3 ${className}`}>
    {children}
  </th>
);

const Stat: React.FC<{ label: string; value: number; tone: 'plain' | 'warn' | 'bad' }> = ({ label, value, tone }) => (
  <div className='bg-white/5 border border-white/10 rounded-xl px-3.5 py-2 min-w-[92px]'>
    <div
      className={`text-lg font-bold tabular-nums ${
        tone === 'bad' ? 'text-red-400' : tone === 'warn' ? 'text-amber-400' : 'text-white'
      }`}
    >
      {value}
    </div>
    <div className='text-[10px] uppercase tracking-wider text-gray-500 mt-0.5'>{label}</div>
  </div>
);

const Badge: React.FC<{ children: React.ReactNode; tone: 'zero' | 'severe' | 'plain' }> = ({ children, tone }) => (
  <span
    className={`inline-flex items-center h-5 px-1.5 rounded text-[9.5px] font-bold tracking-wide ${
      tone === 'zero'
        ? 'bg-[#912018] text-white'
        : tone === 'severe'
          ? 'bg-amber-500/20 text-amber-300'
          : 'bg-white/8 text-gray-400'
    }`}
  >
    {children}
  </span>
);

export default Appeals;
